import React, { useState, useEffect, useCallback } from 'react';
import { ocppService, ocppLogsService, rfidService, getErrorMessage } from '../services/api';
import { toTurkeyTime } from '../utils/dateUtils';

import {
  NativeButton,
  NativeCard,
  NativeCardHeader,
  NativeCardTitle,
  NativeCardDescription,
  NativeCardContent,
  NativeInput,
  NativeSelect,
  NativeBadge,
  NativeAlert,
  NativeModal,
  NativeTable,
  NativeTableHeader,
  NativeTableHead,
  NativeTableBody,
  NativeTableRow,
  NativeTableCell,
  EmptyState
} from '../components/NativeComponents';
import { 
  Loader2, 
  AlertCircle,
  RefreshCw,
  Server,
  Play,
  Square,
  RotateCcw,
  Wifi,
  WifiOff,
  Activity,
  FileText,
  CreditCard,
  AlertTriangle,
  Settings,
  BarChart3,
  Download,
  Search,
  Check,
  X,
  ChevronLeft,
  ChevronRight,
  Zap,
  Clock,
  Cpu,
  Database,
  List,
  Trash2,
  Upload
} from 'lucide-react';
import { toast } from 'sonner';

// Tab component
const TabButton = ({ active, onClick, children, icon: Icon }) => (
  <button
    onClick={onClick}
    style={{
      display: 'flex',
      alignItems: 'center',
      gap: '8px',
      padding: '10px 16px',
      border: 'none',
      borderBottom: active ? '2px solid #2563eb' : '2px solid transparent',
      background: 'transparent',
      color: active ? '#2563eb' : 'var(--text-secondary)',
      fontWeight: active ? '600' : '500',
      fontSize: '14px',
      cursor: 'pointer',
      transition: 'all 0.2s'
    }}
    className={active ? '' : 'hover:bg-slate-100 dark:hover:bg-slate-800'}
  >
    {Icon && <Icon style={{ width: '16px', height: '16px' }} />}
    {children}
  </button>
);

// Pagination component
const Pagination = ({ page, totalPages, onPageChange }) => (
  <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '8px', marginTop: '16px' }}>
    <NativeButton variant="outline" size="sm" onClick={() => onPageChange(page - 1)} disabled={page <= 1}>
      <ChevronLeft style={{ width: '16px', height: '16px' }} />
    </NativeButton>
    <span style={{ padding: '0 16px', color: 'var(--text-primary)', fontSize: '14px' }}>
      Sayfa {page} / {totalPages}
    </span>
    <NativeButton variant="outline" size="sm" onClick={() => onPageChange(page + 1)} disabled={page >= totalPages}>
      <ChevronRight style={{ width: '16px', height: '16px' }} />
    </NativeButton>
  </div>
);

// JSON Viewer component
const JSONViewer = ({ data }) => {
  const [expanded, setExpanded] = useState(false);
  const jsonStr = JSON.stringify(data, null, 2);
  
  if (!data || Object.keys(data).length === 0) {
    return <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>-</span>;
  }
  
  return (
    <div>
      <button
        onClick={() => setExpanded(!expanded)}
        style={{
          background: 'var(--card-bg)',
          border: '1px solid var(--card-border)',
          borderRadius: '4px',
          padding: '4px 8px',
          fontSize: '12px',
          cursor: 'pointer',
          color: '#2563eb'
        }}
      >
        {expanded ? 'Gizle' : 'Göster'}
      </button>
      {expanded && (
        <pre style={{
          marginTop: '8px',
          padding: '12px',
          background: '#1e293b',
          borderRadius: '8px',
          color: '#22c55e',
          fontSize: '11px',
          overflow: 'auto',
          maxHeight: '200px',
          whiteSpace: 'pre-wrap',
          wordBreak: 'break-all'
        }}>
          {jsonStr}
        </pre>
      )}
    </div>
  );
};

// Severity badge
const SeverityBadge = ({ severity }) => {
  const variants = {
    critical: { bg: '#dc2626', text: 'Kritik' },
    error: { bg: '#f97316', text: 'Hata' },
    warning: { bg: '#eab308', text: 'Uyarı' },
    info: { bg: '#3b82f6', text: 'Bilgi' }
  };
  const v = variants[severity] || variants.info;
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: '9999px',
      fontSize: '11px',
      fontWeight: '600',
      backgroundColor: v.bg,
      color: 'white'
    }}>
      {v.text}
    </span>
  );
};

// Status badge
const StatusBadge = ({ status }) => {
  const colors = {
    success: '#22c55e',
    error: '#ef4444',
    pending: '#f59e0b',
    Accepted: '#22c55e',
    Blocked: '#ef4444',
    Expired: '#f97316',
    Invalid: '#6b7280',
    ConcurrentTx: '#8b5cf6'
  };
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: '9999px',
      fontSize: '11px',
      fontWeight: '600',
      backgroundColor: colors[status] || '#6b7280',
      color: 'white'
    }}>
      {status}
    </span>
  );
};

// OCPP durumu Türkçe çeviriler
const OCPP_STATUS_TR = {
  Available: 'Müsait',
  Preparing: 'Hazırlanıyor',
  Charging: 'Şarj Ediyor',
  SuspendedEV: 'Beklemede (EV)',
  SuspendedEVSE: 'Beklemede (EVSE)',
  Finishing: 'Tamamlanıyor',
  Reserved: 'Rezerve',
  Unavailable: 'Kullanılamaz',
  Faulted: 'Arızalı',
  Unknown: 'Bilinmiyor'
};

// Connector status badge
const ConnectorBadge = ({ status }) => {
  const colors = {
    Available: '#22c55e',
    Preparing: '#3b82f6',
    Charging: '#8b5cf6',
    SuspendedEV: '#f59e0b',
    SuspendedEVSE: '#f59e0b',
    Finishing: '#06b6d4',
    Reserved: '#6366f1',
    Unavailable: '#6b7280',
    Faulted: '#ef4444'
  };
  return (
    <span style={{
      padding: '2px 8px',
      borderRadius: '9999px',
      fontSize: '11px',
      fontWeight: '600',
      backgroundColor: colors[status] || '#6b7280',
      color: 'white'
    }}>
      {OCPP_STATUS_TR[status] || status}
    </span>
  );
};

export default function OCPPPage() {
  const [activeTab, setActiveTab] = useState('live');
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  
  // OCPP Status
  const [ocppStatus, setOcppStatus] = useState(null);
  
  // Live monitoring data
  const [liveChargers, setLiveChargers] = useState([]);
  
  // Logs data
  const [logs, setLogs] = useState({ logs: [], total: 0, page: 1, total_pages: 0 });
  const [logsFilter, setLogsFilter] = useState({ action: '', direction: '', status: '', page: 1 });
  
  // Errors data
  const [errors, setErrors] = useState({ errors: [], total: 0, page: 1, total_pages: 0 });
  const [errorsFilter, setErrorsFilter] = useState({ error_code: '', severity: '', acknowledged: '', page: 1 });
  
  // RFID logs
  const [rfidLogs, setRfidLogs] = useState({ logs: [], total: 0, page: 1, total_pages: 0 });
  const [rfidFilter, setRfidFilter] = useState({ id_tag: '', status: '', page: 1 });
  
  // Stats
  const [stats, setStats] = useState(null);
  const [statsPeriod, setStatsPeriod] = useState('24h');
  
  // Critical errors for banner
  const [criticalErrors, setCriticalErrors] = useState([]);
  
  // Modals
  const [remoteStartOpen, setRemoteStartOpen] = useState(false);
  const [remoteStopOpen, setRemoteStopOpen] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [selectedCharger, setSelectedCharger] = useState(null);
  const [saving, setSaving] = useState(false);
  
  const [remoteStartForm, setRemoteStartForm] = useState({ charger_id: '', connector_id: 1, id_tag: '' });
  const [remoteStopForm, setRemoteStopForm] = useState({ charger_id: '', transaction_id: '' });
  const [resetForm, setResetForm] = useState({ charger_id: '', reset_type: 'Soft' });
  
  // RFID kartları listesi (Remote Start için)
  const [rfidCards, setRfidCards] = useState([]);

  // Fetch OCPP status
  const fetchStatus = useCallback(async () => {
    try {
      const data = await ocppService.status();
      setOcppStatus(data);
    } catch (err) {
      console.error('Error fetching OCPP status:', err);
    }
  }, []);

  // Fetch live chargers - gerçek bağlı cihazlar
  const fetchLiveChargers = useCallback(async () => {
    try {
      const data = await ocppService.chargers();
      console.log('OCPP Chargers API response:', data);
      console.log('Online chargers:', data?.filter(c => c.is_online === true));
      // Alfabetik sıralama - istasyon adına göre sabit sıra
      const sortedData = (data || []).sort((a, b) => {
        const nameA = (a.station_name || a.charge_point_id || '').toLowerCase();
        const nameB = (b.station_name || b.charge_point_id || '').toLowerCase();
        return nameA.localeCompare(nameB, 'tr');
      });
      setLiveChargers(sortedData);
    } catch (err) {
      console.error('Error fetching live chargers:', err);
    }
  }, []);

  // Fetch logs
  const fetchLogs = useCallback(async () => {
    try {
      const data = await ocppLogsService.getLogs(logsFilter);
      setLogs(data);
    } catch (err) {
      console.error('Error fetching logs:', err);
    }
  }, [logsFilter]);

  // Fetch errors
  const fetchErrors = useCallback(async () => {
    try {
      const params = { ...errorsFilter };
      if (params.acknowledged === '') delete params.acknowledged;
      else params.acknowledged = params.acknowledged === 'true';
      const data = await ocppLogsService.getErrors(params);
      setErrors(data);
    } catch (err) {
      console.error('Error fetching errors:', err);
    }
  }, [errorsFilter]);

  // Fetch RFID logs
  const fetchRfidLogs = useCallback(async () => {
    try {
      const data = await ocppLogsService.getRfidLogs(rfidFilter);
      setRfidLogs(data);
    } catch (err) {
      console.error('Error fetching RFID logs:', err);
    }
  }, [rfidFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const data = await ocppLogsService.getStats(statsPeriod);
      setStats(data);
    } catch (err) {
      console.error('Error fetching stats:', err);
    }
  }, [statsPeriod]);

  // Fetch critical errors
  const fetchCriticalErrors = useCallback(async () => {
    try {
      const data = await ocppLogsService.getCriticalErrors();
      setCriticalErrors(data || []);
    } catch (err) {
      console.error('Error fetching critical errors:', err);
    }
  }, []);

  // Fetch RFID cards for Remote Start dropdown
  const fetchRfidCards = useCallback(async () => {
    try {
      const data = await rfidService.list();
      // Sadece aktif ve bakiyesi olan kartları al
      const activeCards = (data || []).filter(card => card.is_active);
      setRfidCards(activeCards);
    } catch (err) {
      console.error('Error fetching RFID cards:', err);
    }
  }, []);

  // Initial load
  useEffect(() => {
    const loadData = async () => {
      setLoading(true);
      setError(null);
      try {
        await Promise.all([
          fetchStatus(),
          fetchLiveChargers(),
          fetchCriticalErrors(),
          fetchRfidCards()
        ]);
      } catch (err) {
        setError(getErrorMessage(err));
      } finally {
        setLoading(false);
      }
    };
    loadData();
    
    // Auto refresh every 5 seconds for live data
    const interval = setInterval(() => {
      if (activeTab === 'live') {
        fetchLiveChargers();
        fetchStatus();
      }
      fetchCriticalErrors();
    }, 5000);
    
    return () => clearInterval(interval);
  }, [fetchStatus, fetchLiveChargers, fetchCriticalErrors, fetchRfidCards, activeTab]);

  // Load tab-specific data
  useEffect(() => {
    if (activeTab === 'logs') fetchLogs();
    else if (activeTab === 'errors') fetchErrors();
    else if (activeTab === 'rfid') fetchRfidLogs();
    else if (activeTab === 'stats') fetchStats();
    else if (activeTab === 'live') fetchLiveChargers();
  }, [activeTab, fetchLogs, fetchErrors, fetchRfidLogs, fetchStats, fetchLiveChargers]);

  // Remote start - Geliştirilmiş versiyon
  const handleRemoteStart = async (e) => {
    e.preventDefault();
    
    // 1. Cihaz online kontrolü
    const selectedChargerData = liveChargers.find(c => (c.id || c.charge_point_id) === remoteStartForm.charger_id);
    if (!selectedChargerData) {
      toast.error('Cihaz bulunamadı! Lütfen bağlı bir cihaz seçin.');
      return;
    }
    
    if (selectedChargerData.is_online === false) {
      toast.error('Cihaz çevrimdışı! Şarj başlatmak için cihazın çevrimiçi olması gerekiyor.');
      return;
    }
    
    // 2. RFID kart kontrolü
    if (!remoteStartForm.id_tag) {
      toast.error('Lütfen bir RFID kartı seçin!');
      return;
    }
    
    // 3. Seçilen kartın bakiyesini kontrol et
    const selectedCard = rfidCards.find(c => c.card_id === remoteStartForm.id_tag);
    if (selectedCard && selectedCard.balance <= 0) {
      toast.error(`Bu kartın bakiyesi yetersiz (${selectedCard.balance || 0} TL). Lütfen bakiye yükleyin veya başka kart seçin.`);
      return;
    }
    
    setSaving(true);
    try {
      const result = await ocppService.remoteStart({
        charger_id: remoteStartForm.charger_id,
        connector_id: parseInt(remoteStartForm.connector_id) || 1,
        id_tag: remoteStartForm.id_tag
      });
      
      // Başarı mesajı
      toast.success(
        <div>
          <strong>✅ Şarj Başlatıldı!</strong>
          <br/>
          <span style={{fontSize: '12px'}}>Cihaz: {remoteStartForm.charger_id}</span>
          <br/>
          <span style={{fontSize: '12px'}}>RFID: {remoteStartForm.id_tag}</span>
        </div>,
        { duration: 5000 }
      );
      setRemoteStartOpen(false);
      
      // Listeyi yenile
      setTimeout(() => fetchLiveChargers(), 1000);
      
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      
      // Spesifik hata mesajları - backend'den gelen mesajları aynen göster
      if (errorMsg.includes('bağlı değil') || errorMsg.includes('bulunamadı')) {
        toast.error(
          <div>
            <strong>❌ Cihaz Bağlı Değil</strong>
            <br/>
            <span style={{fontSize: '12px'}}>Cihaz WebSocket bağlantısı kopmuş. Cihazı kontrol edin.</span>
          </div>,
          { duration: 5000 }
        );
      } else if (errorMsg.includes('reddetti') || errorMsg.includes('Rejected')) {
        toast.error(
          <div>
            <strong>❌ Cihaz Şarjı Reddetti</strong>
            <br/>
            <span style={{fontSize: '12px'}}>{errorMsg}</span>
          </div>,
          { duration: 7000 }
        );
      } else if (errorMsg.includes('yanıt alınamadı') || errorMsg.includes('NoResponse')) {
        toast.error(
          <div>
            <strong>❌ Cihazdan Yanıt Yok</strong>
            <br/>
            <span style={{fontSize: '12px'}}>Bağlantı kopmuş olabilir. Sayfayı yenileyip tekrar deneyin.</span>
          </div>,
          { duration: 5000 }
        );
      } else {
        // Backend'den gelen mesajı aynen göster
        toast.error(errorMsg, { duration: 5000 });
      }
    } finally {
      setSaving(false);
    }
  };

  // Remote stop - Geliştirilmiş
  const handleRemoteStop = async (e) => {
    e.preventDefault();
    
    // Cihaz online kontrolü
    const selectedChargerData = liveChargers.find(c => (c.id || c.charge_point_id) === remoteStopForm.charger_id);
    if (selectedChargerData && selectedChargerData.is_online === false) {
      toast.error('Cihaz çevrimdışı! Şarj durdurmak için cihazın çevrimiçi olması gerekiyor.');
      return;
    }
    
    setSaving(true);
    try {
      await ocppService.remoteStop({
        charger_id: remoteStopForm.charger_id,
        transaction_id: parseInt(remoteStopForm.transaction_id)
      });
      
      toast.success(
        <div>
          <strong>✅ Şarj Durduruldu!</strong>
          <br/>
          <span style={{fontSize: '12px'}}>Transaction ID: {remoteStopForm.transaction_id}</span>
        </div>,
        { duration: 5000 }
      );
      setRemoteStopOpen(false);
      setTimeout(() => fetchLiveChargers(), 1000);
    } catch (err) {
      const errorMsg = getErrorMessage(err);
      if (errorMsg.includes('bağlı değil')) {
        toast.error('Cihaz bağlantısı kopmuş. Lütfen cihazı kontrol edin.');
      } else {
        toast.error(errorMsg);
      }
    } finally {
      setSaving(false);
    }
  };

  // Reset
  const handleReset = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      await ocppService.reset({
        charger_id: resetForm.charger_id,
        reset_type: resetForm.reset_type
      });
      toast.success('Reset komutu gönderildi');
      setResetOpen(false);
      fetchLiveChargers();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // Acknowledge error
  const handleAcknowledgeError = async (errorId) => {
    try {
      await ocppLogsService.acknowledgeError(errorId);
      toast.success('Hata onaylandı');
      fetchErrors();
      fetchCriticalErrors();
    } catch (err) {
      toast.error(getErrorMessage(err));
    }
  };

  // Seed mock data
  const handleSeedData = async () => {
    setSaving(true);
    try {
      await ocppLogsService.seedData();
      toast.success('Mock veri oluşturuldu');
      fetchLiveChargers();
      fetchLogs();
      fetchErrors();
      fetchRfidLogs();
      fetchStats();
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setSaving(false);
    }
  };

  // Export CSV
  const exportCSV = (data, filename) => {
    if (!data || data.length === 0) {
      toast.error('Dışa aktarılacak veri yok');
      return;
    }
    const headers = Object.keys(data[0]).join(',');
    const rows = data.map(row => Object.values(row).map(v => 
      typeof v === 'object' ? JSON.stringify(v) : v
    ).join(',')).join('\n');
    const csv = `${headers}\n${rows}`;
    const blob = new Blob([csv], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `${filename}.csv`;
    a.click();
  };

  const openRemoteStartModal = (charger = null) => {
    setRemoteStartForm({ charger_id: charger?.charge_point_id || '', connector_id: 1, id_tag: '' });
    setSelectedCharger(charger);
    setRemoteStartOpen(true);
  };

  const openRemoteStopModal = (charger = null) => {
    setRemoteStopForm({ charger_id: charger?.charge_point_id || '', transaction_id: charger?.current_transaction_id || '' });
    setSelectedCharger(charger);
    setRemoteStopOpen(true);
  };

  const openResetModal = (charger = null) => {
    setResetForm({ charger_id: charger?.charge_point_id || '', reset_type: 'Soft' });
    setSelectedCharger(charger);
    setResetOpen(true);
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '256px' }}>
        <Loader2 className="h-8 w-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error) {
    return (
      <NativeAlert variant="danger" icon={AlertCircle}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <span>{error}</span>
          <NativeButton variant="outline" size="sm" onClick={() => window.location.reload()}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Tekrar Dene
          </NativeButton>
        </div>
      </NativeAlert>
    );
  }

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }} data-testid="ocpp-page">
      {/* Critical Errors Banner */}
      {criticalErrors.length > 0 && (
        <NativeAlert variant="danger" icon={AlertTriangle}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <div>
              <strong>{criticalErrors.length} kritik hata!</strong>
              <span style={{ marginLeft: '8px' }}>
                {criticalErrors[0]?.station_name}: {criticalErrors[0]?.error_code}
              </span>
            </div>
            <NativeButton variant="outline" size="sm" onClick={() => setActiveTab('errors')}>
              Hataları Gör
            </NativeButton>
          </div>
        </NativeAlert>
      )}

      {/* Header */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', alignItems: 'flex-start', gap: '16px' }}>
        <div>
          <h1 className="text-2xl font-bold text-slate-900 dark:text-white" style={{ margin: 0 }}>OCPP İzleme Sistemi</h1>
          <p className="text-slate-500 dark:text-slate-400" style={{ margin: '4px 0 0' }}>Profesyonel şarj istasyonu izleme ve log yönetimi</p>
        </div>
        <div style={{ display: 'flex', gap: '8px' }}>
          <NativeButton variant="outline" onClick={handleSeedData} disabled={saving}>
            <Database style={{ width: '16px', height: '16px' }} />
            Mock Veri
          </NativeButton>
          <NativeButton variant="outline" onClick={() => { 
            fetchStatus(); 
            fetchLiveChargers(); 
            toast.success('Veriler yenilendi!');
          }}>
            <RefreshCw style={{ width: '16px', height: '16px' }} />
            Yenile
          </NativeButton>
        </div>
      </div>

      {/* OCPP Status Cards */}
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: '16px' }}>
        <div style={{ padding: '16px', backgroundColor: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            {ocppStatus?.server_running ? (
              <Wifi style={{ width: '20px', height: '20px', color: '#22c55e' }} />
            ) : (
              <WifiOff style={{ width: '20px', height: '20px', color: '#ef4444' }} />
            )}
            <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>OCPP Sunucu</span>
          </div>
          <p style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
            {ocppStatus?.server_running ? 'Çalışıyor' : 'Kapalı'}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>Port: {ocppStatus?.port || 9000}</p>
        </div>
        
        <div style={{ padding: '16px', backgroundColor: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Cpu style={{ width: '20px', height: '20px', color: '#3b82f6' }} />
            <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Bağlı Cihaz</span>
          </div>
          <p style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
            {liveChargers.filter(c => c.is_online).length} / {liveChargers.length}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>Çevrimiçi / Toplam</p>
        </div>
        
        <div style={{ padding: '16px', backgroundColor: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--card-border)' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <Zap style={{ width: '20px', height: '20px', color: '#8b5cf6' }} />
            <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Aktif Şarj</span>
          </div>
          <p style={{ fontSize: '20px', fontWeight: '700', color: 'var(--text-primary)', margin: 0 }}>
            {liveChargers.filter(c => c.status === 'Charging' || Object.values(c.connector_status || {}).includes('Charging')).length}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>İstasyon şarj ediyor</p>
        </div>
        
        <div style={{ padding: '16px', backgroundColor: criticalErrors.length > 0 ? '#fef2f2' : 'var(--card-bg)', borderRadius: '12px', border: `1px solid ${criticalErrors.length > 0 ? '#fecaca' : 'var(--card-border)'}` }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
            <AlertTriangle style={{ width: '20px', height: '20px', color: criticalErrors.length > 0 ? '#ef4444' : '#6b7280' }} />
            <span style={{ color: 'var(--text-secondary)', fontSize: '13px' }}>Kritik Hata</span>
          </div>
          <p style={{ fontSize: '20px', fontWeight: '700', color: criticalErrors.length > 0 ? '#ef4444' : 'var(--text-primary)', margin: 0 }}>
            {criticalErrors.length}
          </p>
          <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>Onay bekliyor</p>
        </div>
      </div>

      {/* Quick Connection Guide */}
      <NativeCard style={{ background: 'linear-gradient(135deg, #0f172a 0%, #1e293b 100%)', border: '1px solid #334155' }}>
        <NativeCardContent style={{ padding: '20px' }}>
          <div style={{ display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', flexWrap: 'wrap', gap: '20px' }}>
            <div style={{ flex: '1', minWidth: '300px' }}>
              <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '16px' }}>
                <div style={{ 
                  width: '40px', 
                  height: '40px', 
                  borderRadius: '10px', 
                  background: 'linear-gradient(135deg, #22c55e 0%, #16a34a 100%)',
                  display: 'flex',
                  alignItems: 'center',
                  justifyContent: 'center'
                }}>
                  <Zap style={{ width: '22px', height: '22px', color: '#fff' }} />
                </div>
                <div>
                  <h3 style={{ fontSize: '16px', fontWeight: '700', color: '#fff', margin: 0 }}>Cihaz Bağlantı Bilgileri</h3>
                  <p style={{ fontSize: '12px', color: '#94a3b8', margin: '2px 0 0' }}>OCPP 1.6J WebSocket</p>
                </div>
              </div>
              
              <div style={{ marginBottom: '12px' }}>
                <div style={{ fontSize: '12px', color: '#94a3b8', marginBottom: '6px' }}>WebSocket URL</div>
                <code style={{
                  display: 'block',
                  padding: '10px 14px',
                  background: '#0f172a',
                  borderRadius: '8px',
                  color: '#22c55e',
                  fontSize: '13px',
                  fontFamily: 'monospace',
                  wordBreak: 'break-all',
                  border: '1px solid #334155'
                }}>
                  {window.location.origin.replace('https://', 'wss://').replace('http://', 'ws://')}/api/ocpp/&#123;CIHAZ_ID&#125;
                </code>
              </div>
              
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))', gap: '12px' }}>
                <div style={{ padding: '10px', background: '#0f172a', borderRadius: '8px', border: '1px solid #334155' }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Protokol</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#fff' }}>OCPP 1.6J</div>
                </div>
                <div style={{ padding: '10px', background: '#0f172a', borderRadius: '8px', border: '1px solid #334155' }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Subprotocol</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#fff' }}>ocpp1.6</div>
                </div>
                <div style={{ padding: '10px', background: '#0f172a', borderRadius: '8px', border: '1px solid #334155' }}>
                  <div style={{ fontSize: '11px', color: '#94a3b8' }}>Port</div>
                  <div style={{ fontSize: '14px', fontWeight: '600', color: '#fff' }}>443 (WSS)</div>
                </div>
              </div>
            </div>
            
            <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
              <NativeButton 
                variant="outline" 
                size="sm"
                onClick={() => window.open('/documentation', '_self')}
                style={{ background: 'transparent', borderColor: '#475569', color: '#e2e8f0' }}
              >
                <FileText style={{ width: '14px', height: '14px' }} />
                Tam Rehber
              </NativeButton>
              <NativeButton 
                variant="outline" 
                size="sm"
                onClick={() => {
                  const wsUrl = window.location.origin.replace('https://', 'wss://').replace('http://', 'ws://') + '/api/ocpp/';
                  navigator.clipboard.writeText(wsUrl);
                  toast.success('URL kopyalandı!');
                }}
                style={{ background: 'transparent', borderColor: '#475569', color: '#e2e8f0' }}
              >
                <Download style={{ width: '14px', height: '14px' }} />
                URL Kopyala
              </NativeButton>
            </div>
          </div>
        </NativeCardContent>
      </NativeCard>

      {/* Tabs */}
      <div style={{ borderBottom: '1px solid var(--card-border)', display: 'flex', flexWrap: 'wrap', gap: '4px' }}>
        <TabButton active={activeTab === 'live'} onClick={() => setActiveTab('live')} icon={Activity}>Canlı İzleme</TabButton>
        <TabButton active={activeTab === 'logs'} onClick={() => setActiveTab('logs')} icon={FileText}>Mesaj Logları</TabButton>
        <TabButton active={activeTab === 'rfid'} onClick={() => setActiveTab('rfid')} icon={CreditCard}>RFID Logları</TabButton>
        <TabButton active={activeTab === 'errors'} onClick={() => setActiveTab('errors')} icon={AlertTriangle}>Hata Logları</TabButton>
        <TabButton active={activeTab === 'stats'} onClick={() => setActiveTab('stats')} icon={BarChart3}>İstatistikler</TabButton>
        <TabButton active={activeTab === 'locallist'} onClick={() => setActiveTab('locallist')} icon={List}>Yerel Liste</TabButton>
        <TabButton active={activeTab === 'diagnostics'} onClick={() => setActiveTab('diagnostics')} icon={Settings}>Tanılama</TabButton>
      </div>

      {/* Tab Content */}
      {activeTab === 'live' && (
        <NativeCard>
          <NativeCardHeader>
            <NativeCardTitle icon={Activity}>Canlı İstasyon İzleme ({liveChargers.length})</NativeCardTitle>
            <NativeCardDescription>Tüm şarj istasyonlarının anlık durumu</NativeCardDescription>
          </NativeCardHeader>
          <NativeCardContent style={{ padding: 0 }}>
            {liveChargers.length === 0 ? (
              <EmptyState
                icon={WifiOff}
                title="Kayıtlı şarj cihazı yok"
                description="Mock veri oluşturmak için yukarıdaki 'Mock Veri' butonuna tıklayın"
              />
            ) : (
              <NativeTable>
                <NativeTableHeader>
                  <NativeTableHead>Cihaz ID</NativeTableHead>
                  <NativeTableHead>İstasyon</NativeTableHead>
                  <NativeTableHead>Durum</NativeTableHead>
                  <NativeTableHead>Konnektörler</NativeTableHead>
                  <NativeTableHead>Güç (kW)</NativeTableHead>
                  <NativeTableHead>Son Heartbeat</NativeTableHead>
                  <NativeTableHead>Bağlantı</NativeTableHead>
                  <NativeTableHead align="right">İşlemler</NativeTableHead>
                </NativeTableHeader>
                <NativeTableBody>
                  {liveChargers.map((charger) => (
                    <NativeTableRow key={charger.id || charger.charge_point_id}>
                      <NativeTableCell>
                        <code style={{ fontSize: '12px', fontWeight: '600', color: 'var(--text-primary)' }}>{charger.id || charger.charge_point_id}</code>
                      </NativeTableCell>
                      <NativeTableCell>
                        <div>
                          <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{charger.station_name || charger.id || '-'}</div>
                          <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{charger.vendor} {charger.model}</div>
                        </div>
                      </NativeTableCell>
                      <NativeTableCell>
                        <ConnectorBadge status={charger.status} />
                      </NativeTableCell>
                      <NativeTableCell>
                        <div style={{ display: 'flex', gap: '4px', flexWrap: 'wrap' }}>
                          {Object.entries(charger.connector_status || {}).map(([id, status]) => (
                            <span key={id} style={{ fontSize: '10px' }}>
                              #{id}: <ConnectorBadge status={status} />
                            </span>
                          ))}
                        </div>
                      </NativeTableCell>
                      <NativeTableCell>
                        <span style={{ fontWeight: '600', color: (charger.current_power || 0) > 0 ? '#8b5cf6' : 'var(--text-secondary)' }}>
                          {(charger.current_power || 0).toFixed(2)} kW
                        </span>
                      </NativeTableCell>
                      <NativeTableCell>
                        <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>
                          {toTurkeyTime(charger.last_heartbeat)}
                        </div>
                      </NativeTableCell>
                      <NativeTableCell>
                        <NativeBadge variant={charger.is_online !== false ? 'success' : 'danger'}>
                          {charger.is_online !== false ? 'Çevrimiçi' : 'Çevrimdışı'}
                        </NativeBadge>
                      </NativeTableCell>
                      <NativeTableCell align="right">
                        <div style={{ display: 'flex', gap: '4px', justifyContent: 'flex-end' }}>
                          <NativeButton 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setRemoteStartForm({ charger_id: charger.id || charger.charge_point_id, connector_id: 1, id_tag: '' });
                              setRemoteStartOpen(true);
                            }} 
                            title={charger.is_online === false ? "Cihaz çevrimdışı" : "Uzaktan Başlat"}
                            disabled={charger.is_online === false}
                            style={{ 
                              color: charger.is_online === false ? '#9ca3af' : '#22c55e', 
                              borderColor: charger.is_online === false ? '#9ca3af' : '#22c55e',
                              opacity: charger.is_online === false ? 0.5 : 1
                            }}
                          >
                            <Play style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                            Başlat
                          </NativeButton>
                          <NativeButton 
                            variant="outline" 
                            size="sm" 
                            onClick={() => {
                              setRemoteStopForm({ charger_id: charger.id || charger.charge_point_id, transaction_id: charger.current_transaction_id || '' });
                              setRemoteStopOpen(true);
                            }} 
                            title={charger.is_online === false ? "Cihaz çevrimdışı" : "Uzaktan Durdur"}
                            disabled={charger.is_online === false || !charger.current_transaction_id}
                            style={{ 
                              color: (charger.is_online === false || !charger.current_transaction_id) ? '#9ca3af' : '#ef4444', 
                              borderColor: (charger.is_online === false || !charger.current_transaction_id) ? '#9ca3af' : '#ef4444',
                              opacity: (charger.is_online === false || !charger.current_transaction_id) ? 0.5 : 1
                            }}
                          >
                            <Square style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                            Durdur
                          </NativeButton>
                          <NativeButton 
                            variant="ghost" 
                            size="icon" 
                            onClick={() => {
                              setResetForm({ charger_id: charger.id || charger.charge_point_id, reset_type: 'Soft' });
                              setResetOpen(true);
                            }} 
                            title={charger.is_online === false ? "Cihaz çevrimdışı" : "Yeniden Başlat"}
                            disabled={charger.is_online === false}
                          >
                            <RotateCcw style={{ width: '14px', height: '14px' }} />
                          </NativeButton>
                        </div>
                      </NativeTableCell>
                    </NativeTableRow>
                  ))}
                </NativeTableBody>
              </NativeTable>
            )}
          </NativeCardContent>
        </NativeCard>
      )}

      {activeTab === 'logs' && (
        <NativeCard>
          <NativeCardHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div>
                <NativeCardTitle icon={FileText}>OCPP Mesaj Logları</NativeCardTitle>
                <NativeCardDescription>Tüm OCPP 1.6 mesajları ({logs.total} kayıt)</NativeCardDescription>
              </div>
              <NativeButton variant="outline" size="sm" onClick={() => exportCSV(logs.logs, 'ocpp_logs')}>
                <Download style={{ width: '14px', height: '14px' }} />
                CSV
              </NativeButton>
            </div>
          </NativeCardHeader>
          <NativeCardContent>
            {/* Filters */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
              <select
                value={logsFilter.action}
                onChange={(e) => setLogsFilter({ ...logsFilter, action: e.target.value, page: 1 })}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', fontSize: '13px', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
              >
                <option value="">Tüm Aksiyonlar</option>
                <option value="Heartbeat">Heartbeat</option>
                <option value="BootNotification">BootNotification</option>
                <option value="StatusNotification">StatusNotification</option>
                <option value="MeterValues">MeterValues</option>
                <option value="StartTransaction">StartTransaction</option>
                <option value="StopTransaction">StopTransaction</option>
                <option value="Authorize">Authorize</option>
                <option value="DataTransfer">DataTransfer</option>
              </select>
              <select
                value={logsFilter.direction}
                onChange={(e) => setLogsFilter({ ...logsFilter, direction: e.target.value, page: 1 })}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', fontSize: '13px', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
              >
                <option value="">Tüm Yönler</option>
                <option value="incoming">Gelen</option>
                <option value="outgoing">Giden</option>
              </select>
              <select
                value={logsFilter.status}
                onChange={(e) => setLogsFilter({ ...logsFilter, status: e.target.value, page: 1 })}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', fontSize: '13px', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
              >
                <option value="">Tüm Durumlar</option>
                <option value="success">Başarılı</option>
                <option value="error">Hata</option>
              </select>
              <NativeButton variant="outline" size="sm" onClick={fetchLogs}>
                <Search style={{ width: '14px', height: '14px' }} />
                Filtrele
              </NativeButton>
            </div>
            
            {/* Table */}
            <NativeTable>
              <NativeTableHeader>
                <NativeTableHead>Zaman</NativeTableHead>
                <NativeTableHead>Cihaz</NativeTableHead>
                <NativeTableHead>Aksiyon</NativeTableHead>
                <NativeTableHead>Yön</NativeTableHead>
                <NativeTableHead>Durum</NativeTableHead>
                <NativeTableHead>Süre (ms)</NativeTableHead>
                <NativeTableHead>Payload</NativeTableHead>
              </NativeTableHeader>
              <NativeTableBody>
                {logs.logs?.map((log, i) => (
                  <NativeTableRow key={log._id || i}>
                    <NativeTableCell>
                      <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>
                        {toTurkeyTime(log.timestamp)}
                      </div>
                    </NativeTableCell>
                    <NativeTableCell>
                      <code style={{ fontSize: '11px', color: 'var(--text-primary)' }}>{log.charge_point_id}</code>
                    </NativeTableCell>
                    <NativeTableCell>
                      <span style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{log.action}</span>
                    </NativeTableCell>
                    <NativeTableCell>
                      <NativeBadge variant={log.direction === 'incoming' ? 'default' : 'secondary'}>
                        {log.direction === 'incoming' ? '← Gelen' : '→ Giden'}
                      </NativeBadge>
                    </NativeTableCell>
                    <NativeTableCell>
                      <StatusBadge status={log.status} />
                    </NativeTableCell>
                    <NativeTableCell>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{log.processing_time_ms}</span>
                    </NativeTableCell>
                    <NativeTableCell>
                      <JSONViewer data={log.payload} />
                    </NativeTableCell>
                  </NativeTableRow>
                ))}
              </NativeTableBody>
            </NativeTable>
            
            <Pagination
              page={logs.page}
              totalPages={logs.total_pages}
              onPageChange={(p) => setLogsFilter({ ...logsFilter, page: p })}
            />
          </NativeCardContent>
        </NativeCard>
      )}

      {activeTab === 'rfid' && (
        <NativeCard>
          <NativeCardHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div>
                <NativeCardTitle icon={CreditCard}>RFID Yetkilendirme Logları</NativeCardTitle>
                <NativeCardDescription>Kart okutma ve yetkilendirme geçmişi ({rfidLogs.total} kayıt)</NativeCardDescription>
              </div>
              <NativeButton variant="outline" size="sm" onClick={() => exportCSV(rfidLogs.logs, 'rfid_logs')}>
                <Download style={{ width: '14px', height: '14px' }} />
                CSV
              </NativeButton>
            </div>
          </NativeCardHeader>
          <NativeCardContent>
            {/* Filters */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
              <input
                type="text"
                placeholder="Kart ID ara..."
                value={rfidFilter.id_tag}
                onChange={(e) => setRfidFilter({ ...rfidFilter, id_tag: e.target.value })}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', fontSize: '13px', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)', width: '180px' }}
              />
              <select
                value={rfidFilter.status}
                onChange={(e) => setRfidFilter({ ...rfidFilter, status: e.target.value, page: 1 })}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', fontSize: '13px', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
              >
                <option value="">Tüm Durumlar</option>
                <option value="Accepted">Kabul Edildi</option>
                <option value="Blocked">Engellendi</option>
                <option value="Expired">Süresi Doldu</option>
                <option value="Invalid">Geçersiz</option>
                <option value="ConcurrentTx">Eşzamanlı İşlem</option>
              </select>
              <NativeButton variant="outline" size="sm" onClick={fetchRfidLogs}>
                <Search style={{ width: '14px', height: '14px' }} />
                Filtrele
              </NativeButton>
            </div>
            
            <NativeTable>
              <NativeTableHeader>
                <NativeTableHead>Zaman</NativeTableHead>
                <NativeTableHead>İstasyon</NativeTableHead>
                <NativeTableHead>Kart ID</NativeTableHead>
                <NativeTableHead>Kullanıcı</NativeTableHead>
                <NativeTableHead>Sonuç</NativeTableHead>
                <NativeTableHead>Red Sebebi</NativeTableHead>
              </NativeTableHeader>
              <NativeTableBody>
                {rfidLogs.logs?.map((log, i) => (
                  <NativeTableRow key={log._id || i}>
                    <NativeTableCell>
                      <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>
                        {toTurkeyTime(log.timestamp)}
                      </div>
                    </NativeTableCell>
                    <NativeTableCell>
                      <div style={{ color: 'var(--text-primary)' }}>{log.station_name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>Konnektör: {log.connector_id}</div>
                    </NativeTableCell>
                    <NativeTableCell>
                      <code style={{ fontSize: '11px', fontWeight: '600', color: 'var(--text-primary)' }}>{log.id_tag}</code>
                    </NativeTableCell>
                    <NativeTableCell>
                      <span style={{ color: 'var(--text-primary)' }}>{log.user_name || '-'}</span>
                    </NativeTableCell>
                    <NativeTableCell>
                      <StatusBadge status={log.status} />
                    </NativeTableCell>
                    <NativeTableCell>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{log.reject_reason || '-'}</span>
                    </NativeTableCell>
                  </NativeTableRow>
                ))}
              </NativeTableBody>
            </NativeTable>
            
            <Pagination
              page={rfidLogs.page}
              totalPages={rfidLogs.total_pages}
              onPageChange={(p) => setRfidFilter({ ...rfidFilter, page: p })}
            />
          </NativeCardContent>
        </NativeCard>
      )}

      {activeTab === 'errors' && (
        <NativeCard>
          <NativeCardHeader>
            <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', width: '100%' }}>
              <div>
                <NativeCardTitle icon={AlertTriangle}>Hata ve Uyarı Logları</NativeCardTitle>
                <NativeCardDescription>StatusNotification hata kodları ({errors.total} kayıt)</NativeCardDescription>
              </div>
              <NativeButton variant="outline" size="sm" onClick={() => exportCSV(errors.errors, 'error_logs')}>
                <Download style={{ width: '14px', height: '14px' }} />
                CSV
              </NativeButton>
            </div>
          </NativeCardHeader>
          <NativeCardContent>
            {/* Filters */}
            <div style={{ display: 'flex', flexWrap: 'wrap', gap: '12px', marginBottom: '16px' }}>
              <select
                value={errorsFilter.error_code}
                onChange={(e) => setErrorsFilter({ ...errorsFilter, error_code: e.target.value, page: 1 })}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', fontSize: '13px', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
              >
                <option value="">Tüm Hata Kodları</option>
                <option value="ConnectorLockFailure">ConnectorLockFailure</option>
                <option value="EVCommunicationError">EVCommunicationError</option>
                <option value="GroundFailure">GroundFailure</option>
                <option value="HighTemperature">HighTemperature</option>
                <option value="InternalError">InternalError</option>
                <option value="OverCurrentFailure">OverCurrentFailure</option>
                <option value="UnderVoltage">UnderVoltage</option>
                <option value="WeakSignal">WeakSignal</option>
              </select>
              <select
                value={errorsFilter.severity}
                onChange={(e) => setErrorsFilter({ ...errorsFilter, severity: e.target.value, page: 1 })}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', fontSize: '13px', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
              >
                <option value="">Tüm Önemler</option>
                <option value="critical">Kritik</option>
                <option value="error">Hata</option>
                <option value="warning">Uyarı</option>
              </select>
              <select
                value={errorsFilter.acknowledged}
                onChange={(e) => setErrorsFilter({ ...errorsFilter, acknowledged: e.target.value, page: 1 })}
                style={{ padding: '8px 12px', borderRadius: '6px', border: '1px solid var(--card-border)', fontSize: '13px', backgroundColor: 'var(--card-bg)', color: 'var(--text-primary)' }}
              >
                <option value="">Tümü</option>
                <option value="false">Onaylanmamış</option>
                <option value="true">Onaylanmış</option>
              </select>
              <NativeButton variant="outline" size="sm" onClick={fetchErrors}>
                <Search style={{ width: '14px', height: '14px' }} />
                Filtrele
              </NativeButton>
            </div>
            
            <NativeTable>
              <NativeTableHeader>
                <NativeTableHead>Zaman</NativeTableHead>
                <NativeTableHead>İstasyon</NativeTableHead>
                <NativeTableHead>Konnektör</NativeTableHead>
                <NativeTableHead>Hata Kodu</NativeTableHead>
                <NativeTableHead>Önem</NativeTableHead>
                <NativeTableHead>Bilgi</NativeTableHead>
                <NativeTableHead>Onay</NativeTableHead>
                <NativeTableHead align="right">İşlem</NativeTableHead>
              </NativeTableHeader>
              <NativeTableBody>
                {errors.errors?.map((err, i) => (
                  <NativeTableRow key={err._id || i} style={{ backgroundColor: !err.acknowledged && err.severity === 'critical' ? '#fef2f2' : undefined }}>
                    <NativeTableCell>
                      <div style={{ fontSize: '12px', color: 'var(--text-primary)' }}>
                        {toTurkeyTime(err.timestamp)}
                      </div>
                    </NativeTableCell>
                    <NativeTableCell>
                      <div style={{ color: 'var(--text-primary)' }}>{err.station_name}</div>
                      <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{err.charge_point_id}</div>
                    </NativeTableCell>
                    <NativeTableCell>
                      <span style={{ color: 'var(--text-primary)' }}>#{err.connector_id}</span>
                    </NativeTableCell>
                    <NativeTableCell>
                      <code style={{ fontSize: '11px', fontWeight: '600', color: '#ef4444' }}>{err.error_code}</code>
                    </NativeTableCell>
                    <NativeTableCell>
                      <SeverityBadge severity={err.severity} />
                    </NativeTableCell>
                    <NativeTableCell>
                      <span style={{ color: 'var(--text-secondary)', fontSize: '12px' }}>{err.error_info || '-'}</span>
                    </NativeTableCell>
                    <NativeTableCell>
                      {err.acknowledged ? (
                        <span style={{ color: '#22c55e', fontSize: '12px' }}>
                          <Check style={{ width: '14px', height: '14px', display: 'inline' }} /> Onaylı
                        </span>
                      ) : (
                        <span style={{ color: '#f59e0b', fontSize: '12px' }}>Bekliyor</span>
                      )}
                    </NativeTableCell>
                    <NativeTableCell align="right">
                      {!err.acknowledged && (
                        <NativeButton variant="outline" size="sm" onClick={() => handleAcknowledgeError(err._id)}>
                          <Check style={{ width: '14px', height: '14px' }} />
                          Onayla
                        </NativeButton>
                      )}
                    </NativeTableCell>
                  </NativeTableRow>
                ))}
              </NativeTableBody>
            </NativeTable>
            
            <Pagination
              page={errors.page}
              totalPages={errors.total_pages}
              onPageChange={(p) => setErrorsFilter({ ...errorsFilter, page: p })}
            />
          </NativeCardContent>
        </NativeCard>
      )}

      {activeTab === 'stats' && (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '24px' }}>
          {/* Period selector */}
          <div style={{ display: 'flex', gap: '8px' }}>
            {['24h', '7d', '30d'].map((p) => (
              <NativeButton
                key={p}
                variant={statsPeriod === p ? 'default' : 'outline'}
                onClick={() => { setStatsPeriod(p); }}
              >
                {p === '24h' ? 'Son 24 Saat' : p === '7d' ? 'Son 7 Gün' : 'Son 30 Gün'}
              </NativeButton>
            ))}
          </div>

          {stats && (
            <>
              {/* Summary cards */}
              <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(200px, 1fr))', gap: '16px' }}>
                <div style={{ padding: '20px', backgroundColor: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--card-border)' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>Toplam Mesaj</p>
                  <p style={{ fontSize: '28px', fontWeight: '700', color: 'var(--text-primary)', margin: '8px 0 0' }}>{stats.total_messages?.toLocaleString()}</p>
                </div>
                <div style={{ padding: '20px', backgroundColor: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--card-border)' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>Toplam Hata</p>
                  <p style={{ fontSize: '28px', fontWeight: '700', color: '#ef4444', margin: '8px 0 0' }}>{stats.total_errors}</p>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>Hata oranı: %{stats.error_rate?.toFixed(2)}</p>
                </div>
                <div style={{ padding: '20px', backgroundColor: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--card-border)' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>Kritik Hata</p>
                  <p style={{ fontSize: '28px', fontWeight: '700', color: '#dc2626', margin: '8px 0 0' }}>{stats.critical_errors}</p>
                </div>
                <div style={{ padding: '20px', backgroundColor: 'var(--card-bg)', borderRadius: '12px', border: '1px solid var(--card-border)' }}>
                  <p style={{ color: 'var(--text-secondary)', fontSize: '13px', margin: 0 }}>RFID Başarı Oranı</p>
                  <p style={{ fontSize: '28px', fontWeight: '700', color: '#22c55e', margin: '8px 0 0' }}>%{stats.rfid_stats?.success_rate}</p>
                  <p style={{ fontSize: '12px', color: 'var(--text-secondary)', margin: '4px 0 0' }}>{stats.rfid_stats?.accepted}/{stats.rfid_stats?.total} kabul</p>
                </div>
              </div>

              {/* Messages by action */}
              <NativeCard>
                <NativeCardHeader>
                  <NativeCardTitle>Mesaj Dağılımı (Aksiyon)</NativeCardTitle>
                </NativeCardHeader>
                <NativeCardContent>
                  <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px' }}>
                    {stats.messages_by_action?.map((m) => (
                      <div key={m.action} style={{ padding: '8px 12px', backgroundColor: 'var(--card-bg)', border: '1px solid var(--card-border)', borderRadius: '8px' }}>
                        <span style={{ fontWeight: '600', color: 'var(--text-primary)' }}>{m.action}</span>
                        <span style={{ marginLeft: '8px', color: '#3b82f6', fontWeight: '700' }}>{m.count}</span>
                      </div>
                    ))}
                  </div>
                </NativeCardContent>
              </NativeCard>

              {/* Top error stations */}
              <NativeCard>
                <NativeCardHeader>
                  <NativeCardTitle>En Çok Hata Veren İstasyonlar</NativeCardTitle>
                </NativeCardHeader>
                <NativeCardContent>
                  {stats.top_error_stations?.length > 0 ? (
                    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px' }}>
                      {stats.top_error_stations.map((s, i) => (
                        <div key={s.charge_point_id} style={{ display: 'flex', alignItems: 'center', gap: '12px', padding: '8px', backgroundColor: i === 0 ? '#fef2f2' : 'var(--card-bg)', borderRadius: '8px' }}>
                          <span style={{ width: '24px', height: '24px', borderRadius: '50%', backgroundColor: i === 0 ? '#ef4444' : '#6b7280', color: 'white', display: 'flex', alignItems: 'center', justifyContent: 'center', fontSize: '12px', fontWeight: '600' }}>{i + 1}</span>
                          <div style={{ flex: 1 }}>
                            <div style={{ fontWeight: '500', color: 'var(--text-primary)' }}>{s.station_name}</div>
                            <div style={{ fontSize: '11px', color: 'var(--text-secondary)' }}>{s.charge_point_id}</div>
                          </div>
                          <span style={{ fontWeight: '700', color: '#ef4444' }}>{s.count} hata</span>
                        </div>
                      ))}
                    </div>
                  ) : (
                    <p style={{ color: 'var(--text-secondary)' }}>Bu dönemde hata kaydı yok</p>
                  )}
                </NativeCardContent>
              </NativeCard>
            </>
          )}
        </div>
      )}

      {activeTab === 'locallist' && (
        <NativeCard>
          <NativeCardHeader>
            <NativeCardTitle icon={List}>OCPP Yerel Yetkilendirme Listesi</NativeCardTitle>
            <NativeCardDescription>Cihazlara yüklü RFID kart listelerini yönetin (Çevrimdışı yetkilendirme)</NativeCardDescription>
          </NativeCardHeader>
          <NativeCardContent>
            {liveChargers.length === 0 ? (
              <EmptyState
                icon={WifiOff}
                title="Kayıtlı cihaz yok"
                description="OCPP bağlantılı cihaz bulunmuyor"
              />
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: '16px' }}>
                {liveChargers.map((charger) => (
                  <div 
                    key={charger.id || charger.charge_point_id}
                    style={{ 
                      padding: '16px', 
                      backgroundColor: 'var(--card-bg)', 
                      border: '1px solid var(--card-border)', 
                      borderRadius: '12px' 
                    }}
                  >
                    <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', flexWrap: 'wrap', gap: '12px' }}>
                      <div style={{ flex: '1', minWidth: '200px' }}>
                        <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '8px' }}>
                          {charger.is_online !== false ? (
                            <Wifi style={{ width: '16px', height: '16px', color: '#22c55e' }} />
                          ) : (
                            <WifiOff style={{ width: '16px', height: '16px', color: '#ef4444' }} />
                          )}
                          <span style={{ fontWeight: '600', fontSize: '15px', color: 'var(--text-primary)' }}>
                            {charger.station_name || charger.id || charger.charge_point_id}
                          </span>
                        </div>
                        
                        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(120px, 1fr))', gap: '8px', fontSize: '13px' }}>
                          <div>
                            <span style={{ color: 'var(--text-secondary)' }}>Liste Versiyonu: </span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                              {charger.local_list_version || 0}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: 'var(--text-secondary)' }}>Yüklü Kart: </span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                              {charger.local_list_count || 0}
                            </span>
                          </div>
                          <div>
                            <span style={{ color: 'var(--text-secondary)' }}>Son Güncelleme: </span>
                            <span style={{ color: 'var(--text-primary)', fontWeight: '500' }}>
                              {charger.local_list_updated ? toTurkeyTime(charger.local_list_updated) : '-'}
                            </span>
                          </div>
                        </div>
                      </div>
                      
                      <div style={{ display: 'flex', gap: '8px' }}>
                        <NativeButton
                          variant="outline"
                          size="sm"
                          onClick={async () => {
                            if (charger.is_online === false) {
                              toast.error('Cihaz çevrimdışı');
                              return;
                            }
                            try {
                              const token = sessionStorage.getItem('hims_token') || localStorage.getItem('hims_token');
                              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/ocpp/local-list/send/${charger.id || charger.charge_point_id}`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (response.ok) {
                                toast.success('Yerel liste güncellendi');
                                fetchLiveChargers();
                              } else {
                                const err = await response.json();
                                toast.error(err.detail || 'Liste güncellenemedi');
                              }
                            } catch (err) {
                              toast.error('İşlem başarısız');
                            }
                          }}
                          disabled={charger.is_online === false}
                          title="Site RFID kartlarını cihaza gönder"
                        >
                          <Upload style={{ width: '14px', height: '14px', marginRight: '4px' }} />
                          Listeyi Güncelle
                        </NativeButton>
                        
                        <NativeButton
                          variant="ghost"
                          size="sm"
                          onClick={async () => {
                            if (charger.is_online === false) {
                              toast.error('Cihaz çevrimdışı');
                              return;
                            }
                            if (!window.confirm('Cihazdaki yerel listeyi temizlemek istediğinize emin misiniz?')) return;
                            try {
                              const token = sessionStorage.getItem('hims_token') || localStorage.getItem('hims_token');
                              const response = await fetch(`${process.env.REACT_APP_BACKEND_URL || ''}/api/ocpp/local-list/clear/${charger.id || charger.charge_point_id}`, {
                                method: 'POST',
                                headers: { 'Authorization': `Bearer ${token}` }
                              });
                              if (response.ok) {
                                toast.success('Yerel liste temizlendi');
                                fetchLiveChargers();
                              } else {
                                const err = await response.json();
                                toast.error(err.detail || 'Liste temizlenemedi');
                              }
                            } catch (err) {
                              toast.error('İşlem başarısız');
                            }
                          }}
                          disabled={charger.is_online === false}
                          title="Cihazdaki yerel listeyi temizle"
                          style={{ color: '#ef4444' }}
                        >
                          <Trash2 style={{ width: '14px', height: '14px' }} />
                        </NativeButton>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </NativeCardContent>
        </NativeCard>
      )}

      {activeTab === 'diagnostics' && (
        <NativeCard>
          <NativeCardHeader>
            <NativeCardTitle icon={Settings}>İstasyon Tanılama</NativeCardTitle>
            <NativeCardDescription>Cihaz yapılandırması ve uzaktan kontrol</NativeCardDescription>
          </NativeCardHeader>
          <NativeCardContent>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(250px, 1fr))', gap: '16px' }}>
              <div style={{ padding: '20px', backgroundColor: '#f0fdf4', borderRadius: '12px', border: '1px solid #bbf7d0' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '600', color: '#166534' }}>Uzaktan Başlat</h3>
                <p style={{ color: '#166534', fontSize: '13px', margin: '0 0 16px' }}>Şarj oturumunu uzaktan başlatın</p>
                <NativeButton onClick={() => openRemoteStartModal()}>
                  <Play style={{ width: '16px', height: '16px' }} />
                  Şarj Başlat
                </NativeButton>
              </div>
              
              <div style={{ padding: '20px', backgroundColor: '#fef3c7', borderRadius: '12px', border: '1px solid #fde68a' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '600', color: '#92400e' }}>Uzaktan Durdur</h3>
                <p style={{ color: '#92400e', fontSize: '13px', margin: '0 0 16px' }}>Aktif şarj oturumunu durdurun</p>
                <NativeButton variant="warning" onClick={() => openRemoteStopModal()}>
                  <Square style={{ width: '16px', height: '16px' }} />
                  Şarjı Durdur
                </NativeButton>
              </div>
              
              <div style={{ padding: '20px', backgroundColor: '#fef2f2', borderRadius: '12px', border: '1px solid #fecaca' }}>
                <h3 style={{ margin: '0 0 12px', fontSize: '16px', fontWeight: '600', color: '#991b1b' }}>Yeniden Başlat</h3>
                <p style={{ color: '#991b1b', fontSize: '13px', margin: '0 0 16px' }}>Cihazı yeniden başlatın (Yumuşak/Sert)</p>
                <NativeButton variant="danger" onClick={() => openResetModal()}>
                  <RotateCcw style={{ width: '16px', height: '16px' }} />
                  Yeniden Başlat
                </NativeButton>
              </div>
            </div>
          </NativeCardContent>
        </NativeCard>
      )}

      {/* Remote Start Modal - Geliştirilmiş */}
      <NativeModal isOpen={remoteStartOpen} onClose={() => setRemoteStartOpen(false)} title="Uzaktan Şarj Başlat" disabled={saving}>
        <form onSubmit={handleRemoteStart}>
          {/* Cihaz Seçimi - Dropdown */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>
              Cihaz Seçin *
            </label>
            <select
              value={remoteStartForm.charger_id}
              onChange={(e) => setRemoteStartForm({...remoteStartForm, charger_id: e.target.value})}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--card-border)',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            >
              <option value="">-- Cihaz Seçin --</option>
              {liveChargers.map(charger => (
                <option 
                  key={charger.id || charger.charge_point_id} 
                  value={charger.id || charger.charge_point_id}
                  disabled={charger.is_online === false}
                >
                  {charger.station_name || charger.id || charger.charge_point_id} 
                  {charger.is_online === false ? ' (Çevrimdışı)' : ' (Çevrimiçi)'}
                </option>
              ))}
            </select>
            {liveChargers.length === 0 && (
              <p style={{ fontSize: '12px', color: '#f59e0b', marginTop: '4px' }}>
                ⚠️ Bağlı cihaz bulunamadı
              </p>
            )}
          </div>
          
          {/* Konnektör Seçimi */}
          <NativeInput 
            label="Konnektör ID" 
            id="connector_id" 
            type="number" 
            min="1" 
            max="2"
            value={remoteStartForm.connector_id} 
            onChange={(e) => setRemoteStartForm({...remoteStartForm, connector_id: e.target.value})} 
          />
          
          {/* RFID Kart Seçimi - Dropdown */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>
              RFID Kartı Seçin *
            </label>
            <select
              value={remoteStartForm.id_tag}
              onChange={(e) => setRemoteStartForm({...remoteStartForm, id_tag: e.target.value})}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--card-border)',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            >
              <option value="">-- RFID Kartı Seçin --</option>
              {rfidCards.map(card => (
                <option 
                  key={card.card_id} 
                  value={card.card_id}
                  disabled={card.balance <= 0}
                >
                  {card.card_id} - {card.balance?.toFixed(2) || 0} TL 
                  {card.user_name ? ` (${card.user_name})` : ''}
                  {card.balance <= 0 ? ' ⚠️ Yetersiz Bakiye' : ''}
                </option>
              ))}
            </select>
            {rfidCards.length === 0 && (
              <p style={{ fontSize: '12px', color: '#f59e0b', marginTop: '4px' }}>
                ⚠️ Aktif RFID kartı bulunamadı. Önce RFID kartı ekleyin.
              </p>
            )}
            {remoteStartForm.id_tag && (
              <p style={{ fontSize: '12px', color: 'var(--text-secondary)', marginTop: '4px' }}>
                Seçilen kart: <strong>{remoteStartForm.id_tag}</strong>
              </p>
            )}
          </div>
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid var(--card-border)', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={() => setRemoteStartOpen(false)}>İptal</NativeButton>
            <NativeButton 
              type="submit" 
              disabled={saving || !remoteStartForm.charger_id || !remoteStartForm.id_tag}
              style={{ backgroundColor: '#22c55e' }}
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  Başlatılıyor...
                </>
              ) : (
                <>
                  <Play style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  Şarjı Başlat
                </>
              )}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* Remote Stop Modal - Geliştirilmiş */}
      <NativeModal isOpen={remoteStopOpen} onClose={() => setRemoteStopOpen(false)} title="Şarjı Durdur" disabled={saving}>
        <form onSubmit={handleRemoteStop}>
          {/* Cihaz Seçimi - Dropdown */}
          <div style={{ marginBottom: '16px' }}>
            <label style={{ display: 'block', marginBottom: '6px', fontWeight: '500', color: 'var(--text-primary)', fontSize: '14px' }}>
              Cihaz Seçin *
            </label>
            <select
              value={remoteStopForm.charger_id}
              onChange={(e) => {
                const charger = liveChargers.find(c => (c.id || c.charge_point_id) === e.target.value);
                setRemoteStopForm({
                  ...remoteStopForm, 
                  charger_id: e.target.value,
                  transaction_id: charger?.current_transaction_id || ''
                });
              }}
              required
              style={{
                width: '100%',
                padding: '10px 12px',
                borderRadius: '8px',
                border: '1px solid var(--card-border)',
                backgroundColor: 'var(--card-bg)',
                color: 'var(--text-primary)',
                fontSize: '14px'
              }}
            >
              <option value="">-- Cihaz Seçin --</option>
              {liveChargers.filter(c => c.current_transaction_id || c.status === 'Charging').map(charger => (
                <option 
                  key={charger.id || charger.charge_point_id} 
                  value={charger.id || charger.charge_point_id}
                >
                  {charger.station_name || charger.id || charger.charge_point_id} 
                  {charger.current_transaction_id ? ` (TX: ${charger.current_transaction_id})` : ''}
                </option>
              ))}
            </select>
            {liveChargers.filter(c => c.current_transaction_id || c.status === 'Charging').length === 0 && (
              <p style={{ fontSize: '12px', color: '#6b7280', marginTop: '4px' }}>
                ℹ️ Şu anda aktif şarj oturumu olan cihaz bulunmuyor
              </p>
            )}
          </div>
          
          <NativeInput 
            label="Transaction ID" 
            id="transaction_id" 
            type="number" 
            value={remoteStopForm.transaction_id} 
            onChange={(e) => setRemoteStopForm({...remoteStopForm, transaction_id: e.target.value})} 
            placeholder="Otomatik doldurulur" 
            required 
          />
          
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid var(--card-border)', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={() => setRemoteStopOpen(false)}>İptal</NativeButton>
            <NativeButton 
              type="submit" 
              disabled={saving || !remoteStopForm.transaction_id}
              variant="danger"
            >
              {saving ? (
                <>
                  <Loader2 className="animate-spin" style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  Durduruluyor...
                </>
              ) : (
                <>
                  <Square style={{ width: '16px', height: '16px', marginRight: '8px' }} />
                  Şarjı Durdur
                </>
              )}
            </NativeButton>
          </div>
        </form>
      </NativeModal>

      {/* Reset Modal */}
      <NativeModal isOpen={resetOpen} onClose={() => setResetOpen(false)} title="Yeniden Başlat" disabled={saving}>
        <form onSubmit={handleReset}>
          <NativeInput label="Cihaz ID" id="reset_charger_id" value={resetForm.charger_id} onChange={(e) => setResetForm({...resetForm, charger_id: e.target.value})} placeholder="OCPP-001" required />
          <NativeSelect label="Yeniden Başlatma Tipi" id="reset_type" value={resetForm.reset_type} onChange={(e) => setResetForm({...resetForm, reset_type: e.target.value})} options={[{ value: 'Soft', label: 'Yumuşak Sıfırlama' }, { value: 'Hard', label: 'Sert Sıfırlama' }]} />
          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '12px', paddingTop: '16px', borderTop: '1px solid var(--card-border)', marginTop: '16px' }}>
            <NativeButton type="button" variant="outline" onClick={() => setResetOpen(false)}>İptal</NativeButton>
            <NativeButton type="submit" disabled={saving}>
              {saving ? <Loader2 className="animate-spin" style={{ width: '16px', height: '16px' }} /> : 'Sıfırla'}
            </NativeButton>
          </div>
        </form>
      </NativeModal>
    </div>
  );
}
